
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { PurchaseOrderModel } from '@/models/purchase_order';
import { EventBus } from '@/main';

@Component
export default class ImportItemsDialog extends Vue {
  @Prop({ type: Boolean, required: true }) readonly dialog!: boolean;
  @Prop({ type: [String, Number], required: true }) readonly purchaseOrderId!: string | number;

  private importFile: File | null = null;
  private importing = false;
  private downloadingTemplate = false;
  private fileError = '';
  private importSummaryData: any = {};
  private showImportSummaryDialog = false;

  @Watch('dialog')
  onDialogChange(newVal: boolean) {
    if (newVal) {
      // Dialog is opening, reset state
      this.resetState();
    }
  }

  resetState() {
    this.importFile = null;
    this.fileError = '';
    this.importing = false;
    this.downloadingTemplate = false;
    // Reset the file input element
    if (this.$refs.csvFileInput) {
      (this.$refs.csvFileInput as HTMLInputElement).value = '';
    }
  }

  closeDialog() {
    this.resetState();
    this.importSummaryData = {};
    this.showImportSummaryDialog = false;
    this.$emit('update:dialog', false);
  }

  closeSummaryDialog() {
    this.showImportSummaryDialog = false;
    // If import was successful, close the main dialog too
    if (this.importSummaryData.failedRows === 0) {
      this.closeDialog();
    } else {
      // If there were errors, keep the main dialog open but reset file selection
      this.resetState();
    }
  }

  async downloadTemplate() {
    this.downloadingTemplate = true;
    try {
      const blob = await PurchaseOrderModel.downloadItemsTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'purchase_order_items_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Download failed: ${error.message}`, 'error');
    } finally {
      this.downloadingTemplate = false;
    }
  }

  openFileInput() {
    (this.$refs.csvFileInput as HTMLInputElement).click();
  }

  handleFileSelection(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) {
      this.importFile = null;
      this.fileError = '';
      return;
    }

    const file = files[0];

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.fileError = 'Please select a valid CSV file.';
      this.importFile = null;
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      this.fileError = 'File size must be less than 5MB.';
      this.importFile = null;
      return;
    }

    this.importFile = file;
    this.fileError = '';
  }

  clearFileSelection() {
    this.importFile = null;
    this.fileError = '';
    if (this.$refs.csvFileInput) {
      (this.$refs.csvFileInput as HTMLInputElement).value = '';
    }
  }

  formatFileSize(size: number): string {
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async importItems() {
    if (!this.importFile) return;

    this.importing = true;
    try {
      const po = new PurchaseOrderModel({ id: Number(this.purchaseOrderId) });
      const response = await po.importItems(this.importFile);
      
      this.importSummaryData = response;
      this.showImportSummaryDialog = true;

      // Only emit success if there were no failed rows
      if (response.failedRows === 0) {
        this.$emit('import-success');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'An unknown error occurred.';
      this.fileError = `Import failed: ${message}`;
      if (error.response?.data?.errorDetails) {
        this.importSummaryData = error.response.data;
        this.showImportSummaryDialog = true;
      } else {
        EventBus.$emit('show-snackbar', this.fileError, 'error');
      }
    } finally {
      this.importing = false;
    }
  }
}
